import { faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useContext } from 'react';

import { AnimatedTransition } from '../../../../components/animated-transition';
import { Item } from '../../../../components/multi-select/multi-select-list-filters';
import { MultiSelectListFilters } from '../../../../components/multi-select/multi-select-list-filters';
import { Radio } from '../../../../components/radio';
import { SurfaceMarkingContext } from '../../../../providers/otc-trader/surface-marking-provider';
import Utils from '../../../../utils/immersive_utils';
import './styles.scss';

type VolatilitySurfaceMarkingSidebarDropdownProps = {
  itemTitle: string;
  items: Item[];
  selectedItems: Item[];
  handleChange: (item: Item[]) => void;
  classNames?: string;
  isRadio?: boolean;
  isRadioDisabled?: boolean;
  canUpdate?: boolean;
  defaultValue?: Item | Item[];
};

export const VolatilitySurfaceMarkingSidebarDropdown = ({
  itemTitle,
  items,
  selectedItems,
  handleChange,
  classNames,
  defaultValue,
  isRadio = false,
  canUpdate = false,
}: VolatilitySurfaceMarkingSidebarDropdownProps) => {
  const { volatilityOptions, surfaceLoading } = useContext(SurfaceMarkingContext);
  const [isDropdownOpen, setIDropdownOpen] = useState(false);
  const [currentSelectedItems, setCurrentSelectedItems] = useState<Item[]>(selectedItems);

  const handleSelectDefault = () => {
    if (!defaultValue) return;

    if (isRadio) {
      const selectedDefault = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;
      setCurrentSelectedItems([selectedDefault]);
      handleChange([selectedDefault]);
    } else {
      const selectedDefaults = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      setCurrentSelectedItems(selectedDefaults);
      handleChange(selectedDefaults);
    }
  };

  useEffect(() => {
    if (defaultValue && canUpdate) {
      if (JSON.stringify(defaultValue) === JSON.stringify(currentSelectedItems)) return;

      handleSelectDefault();
    }
  }, [defaultValue]);

  useEffect(() => {
    handleSelectDefault();
  }, []);

  const checkAndSetTenor = () => {
    if (volatilityOptions?.surface?.vols) {
      const selectedTenorsWithValues = items.filter((item) =>
        volatilityOptions.surface!.vols.some(
          (vol: any) => vol.tenor === item.value && vol.value !== '',
        ),
      );

      setCurrentSelectedItems(selectedTenorsWithValues);
      handleChange(selectedTenorsWithValues);
    }
  };

  useEffect(() => {
    if (surfaceLoading) return;

    if (!isRadio) {
      checkAndSetTenor();
    }
  }, [volatilityOptions.surface, surfaceLoading]);

  useEffect(() => {
    if (surfaceLoading || isRadio) return;
    checkAndSetTenor();
  }, [volatilityOptions.surface, surfaceLoading, isRadio, items, handleChange]);

  const toggleRiskSettingsItem = () => {
    setIDropdownOpen((state) => !state);
  };

  const handleRadioChange = (item: Item) => {
    setCurrentSelectedItems([item]);
    handleChange([item]);
  };

  const handleMultiSelectChange = (items: Item[]) => {
    setCurrentSelectedItems(items);
    handleChange(items);
  };

  return (
    <div className="volatility-dropdown">
      <div className="volatility-dropdown-header" onClick={toggleRiskSettingsItem}>
        <span>{itemTitle}</span>
        <FontAwesomeIcon icon={isDropdownOpen ? faAngleUp : faAngleDown} />
      </div>
      {isDropdownOpen && (
        <AnimatedTransition className="volatility-dropdown-content">
          {isRadio ? (
            <div className="volatility-dropdown-content-radios-data">
              {items.map((item, index) => (
                <div
                  key={`${item.label}-${index}`}
                  className="volatility-dropdown-content-area-radio"
                >
                  <Radio
                    label={item.label}
                    checked={currentSelectedItems.some(
                      (selectedItem) => selectedItem.value === item.value,
                    )}
                    onChange={() => handleRadioChange(item)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <MultiSelectListFilters
              items={items.map((item) => ({ ...item, label: Utils.truncateLabel(item.label, 11) }))}
              selectedItems={currentSelectedItems}
              setSelectedItems={handleMultiSelectChange}
              classNames={`${classNames ? classNames : 'volatility-dropdown-content-multi-select'}`}
            />
          )}
        </AnimatedTransition>
      )}
    </div>
  );
};
